import { supabase, supabaseRpc, getCurrentUserId } from '@/lib/supabase'
import { newHouseholdCohortFields } from '@/services/businessCohort'
import { debugLog } from '@/utils/logger'
import type { Database } from '@/lib/database.types'
import { User } from '@/models'
import { householdMigrationService } from './household-migration.service'
import { detectLocale } from '@/i18n'

type HouseholdRow = Database['public']['Tables']['households']['Row']
type HouseholdInsert = Database['public']['Tables']['households']['Insert'] & { description?: string }
type HouseholdUpdate = Database['public']['Tables']['households']['Update'] & { description?: string }
type HouseholdInvitationRow = Database['public']['Tables']['household_invitations']['Row'] & {
    invitation_code?: string
    invitee_id?: string
    accepted_at?: string
}

/**
 * Le code d'invitation : 6 chiffres.
 *
 * C'était 8 caractères alphanumériques. Mesuré en base : la moitié des
 * invitations sont acceptées en 3,2 minutes, aucune n'est jamais passée par
 * e-mail. C'est un code qu'on dicte, et « O ou zéro, I ou 1 » est une question
 * qu'on ne devrait jamais avoir à poser.
 *
 * ⚠️ `crypto.getRandomValues` et non `Math.random()`. Sur 10^6 valeurs, la
 * prédictibilité d'un générateur pseudo-aléatoire cesse d'être théorique : la
 * suite de Math.random se reconstruit à partir de quelques sorties observées,
 * et un invitant peut en observer autant qu'il veut. Le rejet des valeurs
 * hautes évite le biais modulo, qui rendrait certains codes plus probables.
 */
const INVITATION_CODE_DIGITS = 6

function generateInvitationCode(): string {
    const max = 10 ** INVITATION_CODE_DIGITS
    const limit = Math.floor(0xffffffff / max) * max
    const buf = new Uint32Array(1)
    let draw = limit
    do {
        crypto.getRandomValues(buf)
        draw = buf[0]
    } while (draw >= limit)
    return String(draw % max).padStart(INVITATION_CODE_DIGITS, '0')
}

export const supabaseHouseholdService = {
    /**
     * Create a new household without authentication (for onboarding flow)
     * This will be linked to a user after they are created
     */
    async createHouseholdWithoutAuth(name: string, description?: string): Promise<HouseholdRow> {
        try {
            // Use RPC to create household with a temporary owner_id
            // This requires a special RPC function on the Supabase side
            const { data, error } = await supabaseRpc
                .rpc('create_household_without_auth', {
                    household_name: name.trim(),
                    household_description: description?.trim() || '',
                    user_name: 'Temporary User' // Required parameter
                })

            if (error) {
                console.error('Error creating household without auth:', error)
                throw error
            }

            if (!data) {
                throw new Error('No household data returned from creation')
            }

            // console.log('Household created successfully with temporary owner:', data)
            // Cast the Json result to HouseholdRow
            return data as unknown as HouseholdRow
        } catch (error) {
            console.error('Error creating household without auth:', error)
            throw error
        }
    },

    /**
     * Create a new household for onboarding
     * Uses a special RPC function to bypass RLS policies
     */
    async createHouseholdForOnboarding(name: string, ownerId: string, description?: string): Promise<HouseholdRow> {
        try {
            // console.log('Creating household for onboarding with owner ID:', ownerId);

            // Direct insertion now that FK points to user_profiles
            const { data, error } = await supabase
                .from('households')
                .insert({
                    name: name.trim(),
                    description: description?.trim() || '',
                    owner_id: ownerId,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    // Tout foyer créé par cette version relève du modèle par
                    // profil, quel que soit le chemin qui y mène.
                    ...newHouseholdCohortFields()
                })
                .select()
                .single()

            if (error) {
                console.error('Error creating household for onboarding:', error)
                throw error
            }

            if (!data) {
                throw new Error('No household data returned from creation')
            }

            // Add the owner as a member of the household
            const { error: memberError } = await supabase
                .from('household_members')
                .insert({
                    household_id: data.id,
                    user_id: ownerId,
                    role: 'owner',
                    joined_at: new Date().toISOString(),
                    created_at: new Date().toISOString(),
                    created_by: ownerId
                })

            if (memberError) {
                console.error('Error adding owner as household member during onboarding:', memberError)
                // This is critical - without membership, the user won't have RLS access to create children
                throw new Error('Failed to add owner as household member: ' + memberError.message)
            }

            // console.log('Household created successfully for onboarding:', data)
            return data as HouseholdRow
        } catch (error) {
            console.error('Error creating household for onboarding:', error)
            throw error
        }
    },

    /**
     * Create a new household
     */
    async createHousehold(name: string, description?: string): Promise<HouseholdRow> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        const householdData: HouseholdInsert = {
            name: name.trim(),
            owner_id: user.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            ...newHouseholdCohortFields()
        }

        // Add description if provided
        if (description) {
            (householdData as any).description = description.trim()
        }

        const { data, error } = await supabase
            .from('households')
            .insert(householdData)
            .select()
            .single()

        if (error) throw error

        // Add the owner as a member of the household
        const { error: memberError } = await supabase
            .from('household_members')
            .insert({
                household_id: data.id,
                user_id: user.id,
                role: 'owner',
                joined_at: new Date().toISOString(),
                created_at: new Date().toISOString(),
                created_by: user.id
            })

        if (memberError) {
            console.error('Error adding owner as household member:', memberError)
            // Don't throw error - household was created successfully
        }

        return data as HouseholdRow
    },

    /**
     * Get user's households (owned or member)
     */
    async getUserHouseholds(): Promise<HouseholdRow[]> {
        // Lecture protégée par RLS : on n'a besoin QUE d'un id pour filtrer.
        // getUser() ferait valider le jeton par GoTrue sur le réseau à chaque
        // appel ; getCurrentUserId() lit la session locale.
        const userId = await getCurrentUserId()
        if (!userId) throw new Error('User not authenticated')
        const user = { id: userId }

        // Récupérer d'abord les households où l'utilisateur est propriétaire
        const { data: ownedHouseholds, error: ownedError } = await supabase
            .from('households')
            .select('*')
            .eq('owner_id', user.id)
            .order('created_at', { ascending: false });

        if (ownedError) {
            console.error('❌ [HouseholdService] Error fetching owned households:', ownedError)
            throw ownedError
        }

        // console.log('✅ [HouseholdService] Owned households found:', ownedHouseholds?.length || 0)

        // Récupérer les households où l'utilisateur est membre
        const { data: memberHouseholds, error: memberError } = await supabase
            .from('households')
            .select(`
                *,
                household_members!inner(user_id)
            `)
            .eq('household_members.user_id', user.id)
            .order('created_at', { ascending: false });

        if (memberError) throw memberError;

        // Combiner et dédupliquer les résultats
        const allHouseholds = [...(ownedHouseholds || []), ...(memberHouseholds || [])];
        const uniqueHouseholds = allHouseholds.filter((household, index, self) =>
            index === self.findIndex(h => h.id === household.id)
        );

        return uniqueHouseholds as HouseholdRow[];
    },

    /**
     * Get user's primary household (first one or owned one)
     */
    async getUserHousehold(userId?: string): Promise<HouseholdRow | null> {
        // Lecture protégée par RLS : session locale, pas d'aller-retour GoTrue.
        const targetUserId = userId || (await getCurrentUserId())
        if (!targetUserId) throw new Error('User not authenticated')

        // First try to get owned household
        const { data: ownedHousehold, error: ownedError } = await supabase
            .from('households')
            .select('*')
            .eq('owner_id', targetUserId)
            .order('created_at', { ascending: false })
            .limit(1)
            .single()

        if (!ownedError && ownedHousehold) {
            return ownedHousehold as HouseholdRow
        }

        try {
            // If no owned household, get first household where user is member
            const { data: memberHousehold, error: memberError } = await supabase
                .from('household_members')
                .select(`
                    household_id
                `)
                .eq('user_id', targetUserId)
                .order('joined_at', { ascending: false })
                .limit(1)
                .single()

            if (memberError || !memberHousehold) {
                return null
            }

            // Récupérer les détails du foyer séparément
            const { data: household, error: householdError } = await supabase
                .from('households')
                .select('*')
                .eq('id', memberHousehold.household_id)
                .single()

            if (householdError) {
                return null
            }

            return household as HouseholdRow
        } catch (error) {
            console.error('Error getting household for member:', error)
            return null
        }
    },

    /**
     * Get household by ID
     */
    async getHousehold(householdId: string): Promise<HouseholdRow | null> {
        const { data, error } = await supabase
            .from('households')
            .select('*')
            .eq('id', householdId)
            .single()

        if (error) {
            if (error.code === 'PGRST116') return null // Not found
            throw error
        }
        return data as HouseholdRow
    },

    /**
     * Update household
     */
    async updateHousehold(householdId: string, updates: HouseholdUpdate): Promise<HouseholdRow> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        const updateData: HouseholdUpdate = {
            ...updates,
            updated_at: new Date().toISOString()
        }

        const { data, error } = await supabase
            .from('households')
            .update(updateData)
            .eq('id', householdId)
            .eq('owner_id', user.id) // Only owner can update
            .select()
            .single()

        if (error) throw error
        return data as HouseholdRow
    },

    /**
     * Delete household
     */
    async deleteHousehold(householdId: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        const { error } = await supabase
            .from('households')
            .delete()
            .eq('id', householdId)
            .eq('owner_id', user.id) // Only owner can delete

        if (error) throw error
    },

    /**
     * Create household invitation with unique code (simplified - no email)
     */
    async createInvitation(householdId: string, inviteeEmail: string): Promise<string> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // Generate unique invitation code
        const invitationCode = generateInvitationCode()

        // Use RPC function to create invitation (bypasses RLS issues)
        const { data, error } = await supabaseRpc
            .rpc('create_household_invitation', {
                p_household_id: householdId,
                p_invitee_email: inviteeEmail.toLowerCase().trim(),
                p_invitation_code: invitationCode
            })

        if (error) {
            console.error('RPC create_household_invitation error:', error)
            throw error
        }

        // Send invitation email via Edge Function
        try {
            // Type the response data
            const responseData = data as { household_name?: string; inviter_name?: string; success?: boolean }

            const { error: emailError } = await supabase.functions.invoke('send-invitation-email-mailtrap', {
                body: {
                    invitation_code: invitationCode,
                    household_name: responseData?.household_name || 'Votre foyer',
                    inviter_name: responseData?.inviter_name || 'Un parent',
                    invitee_email: inviteeEmail.toLowerCase().trim()
                }
            })

            if (emailError) {
                console.warn('Failed to send invitation email:', emailError)
                // Don't throw error - invitation was created successfully
            } else {
                // console.log('✅ Invitation email sent successfully')
            }
        } catch (emailError) {
            console.warn('Failed to send invitation email:', emailError)
            // Don't throw error - invitation was created successfully
        }

        return invitationCode
    },

    /**
     * Create simple invitation code (no email required)
     */
    async createSimpleInvitationCode(householdId: string): Promise<string> {
        // console.log('🔍 [createSimpleInvitationCode] Starting with householdId:', householdId)

        const { data: { user } } = await supabase.auth.getUser()
        if (!user) {
            console.error('❌ [createSimpleInvitationCode] User not authenticated')
            throw new Error('User not authenticated')
        }

        // console.log('✅ [createSimpleInvitationCode] User authenticated:', user.id)

        // Generate unique invitation code
        const invitationCode = generateInvitationCode()
        // console.log('🔑 [createSimpleInvitationCode] Generated code:', invitationCode)

        // Get household and user info
        // console.log('🏠 [createSimpleInvitationCode] Fetching household...')
        const { data: household, error: householdError } = await supabase
            .from('households')
            .select('id, name, owner_id')
            .eq('id', householdId)
            .single()

        if (householdError || !household) {
            console.error('❌ [createSimpleInvitationCode] Error fetching household:', householdError)
            throw new Error('Household not found')
        }

        // console.log('✅ [createSimpleInvitationCode] Household found:', household.name)

        // Verify user owns the household
        if (household.owner_id !== user.id) {
            console.error('❌ [createSimpleInvitationCode] User is not the owner')
            throw new Error('You do not have permission to create invitations for this household')
        }

        // Use RPC function instead of direct insert to bypass RLS issues
        // console.log('📝 [createSimpleInvitationCode] Creating invitation via RPC...')
        const { error } = await supabaseRpc
            .rpc('create_household_invitation', {
                p_household_id: householdId,
                p_invitee_email: 'code@invitation.local',
                p_invitation_code: invitationCode
            })

        if (error) {
            console.error('❌ [createSimpleInvitationCode] RPC error:', error)
            throw error
        }

        // console.log('✅ [createSimpleInvitationCode] Invitation created successfully')
        return invitationCode
    },

    /**
     * Accept invitation using code (for existing users)
     */
    async acceptInvitation(invitationCode: string): Promise<HouseholdRow> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // console.log('🔍 [AcceptInvitation] Looking for invitation code:', invitationCode.toUpperCase())

        // Use RPC function to accept invitation (bypasses RLS issues)
        const { data, error } = await supabaseRpc
            .rpc('accept_household_invitation', {
                p_invitation_code: invitationCode.toUpperCase()
            })

        if (error) {
            console.error('❌ [AcceptInvitation] RPC error:', error)
            throw new Error(error.message || 'Invalid or expired invitation code')
        }

        // Type the response data
        const responseData = data as { household?: HouseholdRow; success?: boolean; message?: string }

        if (!responseData || !responseData.household) {
            throw new Error('Invalid or expired invitation code')
        }

        // console.log('✅ [AcceptInvitation] Invitation accepted successfully:', responseData.household)
        return responseData.household
    },

    /**
     * Vérifie un code d'invitation SANS rien accepter et SANS être connecté.
     *
     * C'est la moitié lecture d'`acceptInvitation`. Elle existe parce que le
     * parcours « rejoindre une famille » demandait de créer un compte avant
     * d'avoir vérifié quoi que ce soit : un code faux se découvrait après.
     *
     * Rend de quoi reconnaître le foyer, jamais le prénom d'un enfant.
     */
    async previewInvitation(invitationCode: string): Promise<{
        id: string
        name: string
        ownerName?: string
        childrenCount?: number
    }> {
        const { data, error } = await supabaseRpc
            .rpc('preview_household_invitation', {
                p_invitation_code: invitationCode.trim().toUpperCase()
            })

        if (error) {
            // Panne réelle (réseau, droit manquant, fonction absente). Les REFUS,
            // eux, arrivent en `data.error` : la fonction doit réussir pour que
            // le compteur de tentatives qu'elle écrit survive à l'appel.
            throw new Error(error.message || 'Invalid invitation code')
        }

        const preview = data as {
            id?: string
            name?: string
            ownerName?: string | null
            childrenCount?: number
            error?: string
        } | null

        // Le message est le contrat : classifyJoinError le lit pour choisir
        // l'écran (invalide / expiré / déjà utilisé / trop de tentatives).
        if (preview?.error) {
            throw new Error(preview.error)
        }

        if (!preview?.id) {
            throw new Error('Invalid invitation code')
        }

        return {
            id: preview.id,
            name: preview.name || '',
            ownerName: preview.ownerName || undefined,
            childrenCount: preview.childrenCount,
        }
    },

    /**
     * Transfer household ownership to another member
     */
    async transferOwnership(householdId: string, newOwnerId: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // Use RPC function to transfer ownership
        const { error } = await supabaseRpc
            .rpc('transfer_household_ownership', {
                p_household_id: householdId,
                p_new_owner_id: newOwnerId
            })

        if (error) {
            console.error('Transfer ownership error:', error)
            throw new Error(error.message || 'Failed to transfer ownership')
        }

        // console.log('✅ [TransferOwnership] Ownership transferred successfully')
    },

    /**
     * Get household members
     */
    async getHouseholdMembers(householdId: string): Promise<User[]> {
        try {
            debugLog('🔍 [HouseholdService] Getting household members for household:', householdId)

            // Ensure owner is a member before fetching members
            const ownerAdded = await householdMigrationService.ensureOwnerIsMember(householdId)
            debugLog('✅ [HouseholdService] Owner added as member?', ownerAdded)

            // Use RPC to get household members with profile data (includes avatar_url)
            const { data: members, error: membersError } = await supabaseRpc
                .rpc('get_household_members', {
                    p_household_id: householdId
                })

            debugLog('📊 [HouseholdService] Household members RPC result:', members)
            debugLog('❌ [HouseholdService] RPC error:', membersError)

            if (membersError) {
                console.error('❌ [HouseholdService] RPC Error fetching household members:', membersError)
                debugLog('🔄 [HouseholdService] Falling back to direct query...')
                
                // Fallback: try direct query
                try {
                    const { data: directMembers, error: directError } = await supabase
                        .from('household_members')
                        .select('user_id, role, joined_at')
                        .eq('household_id', householdId)
                    
                    if (directError) {
                        console.error('❌ [HouseholdService] Direct query also failed:', directError)
                        return []
                    }
                    
                    if (!directMembers || directMembers.length === 0) {
                        debugLog('⚠️ [HouseholdService] No members found via direct query for household:', householdId)
                        return []
                    }
                    
                    debugLog('✅ [HouseholdService] Direct query succeeded, found', directMembers.length, 'members')
                    
                    // Use direct query result
                    const userIds = directMembers.map(m => m.user_id)
                    const { data: userProfiles, error: profilesError } = await supabase
                        .from('user_profiles')
                        .select('id, name, email, avatar_url, language')
                        .in('id', userIds)

                    if (profilesError) {
                        console.error('❌ [HouseholdService] Error fetching user profiles:', profilesError)
                        return []
                    }

                    const memberUsers = directMembers.map(member => {
                        const profile = userProfiles?.find(p => p.id === member.user_id)
                        return {
                            id: member.user_id,
                            name: profile?.name || 'Utilisateur inconnu',
                            email: profile?.email || '',
                            photoURL: profile?.avatar_url || '',
                            language: profile?.language || 'fr',
                            notification_preferences: {
                                daily_reminder: false,
                                point_assignments: false,
                                reward_unlocked: false
                            }
                        }
                    });

                    debugLog('✅ [HouseholdService] Fallback successful, returning', memberUsers.length, 'members')
                    return memberUsers;
                } catch (fallbackError) {
                    console.error('❌ [HouseholdService] Fallback also failed:', fallbackError)
                    return []
                }
            }

            if (!members || members.length === 0) {
                debugLog('⚠️ [HouseholdService] No members found for household:', householdId)
                return []
            }

            // Helper to normalize avatar URL (accepts full URL or storage path)
            const normalizeAvatarUrl = (url?: string | null): string => {
                if (!url) return ''
                // If already an absolute URL, return as-is
                if (/^https?:\/\//i.test(url)) return url
                // Otherwise, treat it as a path inside the 'avatars' bucket
                try {
                    const { data } = supabase.storage
                        .from('avatars')
                        .getPublicUrl(url.startsWith('avatars/') ? url.replace(/^avatars\//, '') : url)
                    return data.publicUrl
                } catch {
                    return ''
                }
            }

            // Transform the RPC result into User objects directly (no extra query)
            const memberUsers = members.map(member => {
                const avatarUrl = normalizeAvatarUrl((member as any).avatar_url)
                return {
                    id: (member as any).user_id,
                    name: (member as any).name || 'Utilisateur inconnu',
                    email: (member as any).email || '',
                    photoURL: avatarUrl,
                    language: (member as any).language || 'fr',
                    notification_preferences: {
                        daily_reminder: Boolean((member as any).daily_reminder),
                        point_assignments: Boolean((member as any).point_assignments),
                        reward_unlocked: Boolean((member as any).reward_unlocked)
                    }
                }
            })

            // console.log('✅ Final member users:', memberUsers)

            return memberUsers;
        } catch (error) {
            console.error('Error getting household members:', error)
            return []
        }
    },

    /**
     * Remove household member
     */
    async removeMember(householdId: string, userId: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // Verify user owns the household
        const household = await this.getHousehold(householdId)
        if (!household || household.owner_id !== user.id) {
            throw new Error('You do not have permission to remove members from this household')
        }

        // Cannot remove the owner
        if (userId === user.id) {
            throw new Error('Cannot remove the household owner')
        }

        // `.select()` après le delete pour lire les lignes RÉELLEMENT supprimées :
        // un DELETE filtré à 0 ligne par la RLS ne remonte AUCUNE erreur côté
        // PostgREST (cf. migration household_members_owner_can_remove), donc se
        // fier au seul `error` fait passer un échec silencieux pour un succès.
        const { data, error } = await supabase
            .from('household_members')
            .delete()
            .eq('household_id', householdId)
            .eq('user_id', userId)
            .select('user_id')

        if (error) throw error
        if (!data || data.length === 0) {
            throw new Error('Member was not removed (no row deleted)')
        }
    },

    /**
     * Get pending invitations for a household
     * Note: Cette méthode retourne un tableau vide en cas d'erreur RLS
     * pour éviter de planter l'application
     */
    async getHouseholdInvitations(householdId: string): Promise<HouseholdInvitationRow[]> {
        // Lecture protégée par RLS : l'id ne sert qu'à vérifier qu'on est connecté.
        if (!(await getCurrentUserId())) throw new Error('User not authenticated')

        // console.log('🔍 [getHouseholdInvitations] Fetching invitations for household:', householdId)

        try {
            // Essayer d'abord avec une requête simple
            const { data, error } = await supabase
                .from('household_invitations')
                .select('*')
                .eq('household_id', householdId)
                .order('created_at', { ascending: false })

            if (error) {
                console.error('❌ [getHouseholdInvitations] RLS Error:', error)
                // console.log('🔄 [getHouseholdInvitations] RLS blocking access, returning empty array')
                return []
            }

            // console.log('✅ [getHouseholdInvitations] Raw data:', data)

            // Filtrer côté client pour éviter les problèmes RLS
            const now = new Date().toISOString()
            const filteredData = (data || []).filter(inv =>
                inv.status === 'pending' &&
                (!inv.expires_at || inv.expires_at > now)
            )

            // console.log('✅ [getHouseholdInvitations] Filtered data:', filteredData)
            return filteredData
        } catch (error) {
            console.error('❌ [getHouseholdInvitations] Unexpected error:', error)
            // Retourner un tableau vide en cas d'erreur plutôt que de planter
            return []
        }
    },

    /**
     * Cancel invitation
     */
    async cancelInvitation(invitationId: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        const { error } = await supabase
            .from('household_invitations')
            .update({ status: 'cancelled' })
            .eq('id', invitationId)
            .eq('inviter_id', user.id) // Only inviter can cancel

        if (error) throw error
    },

    /**
     * Delete invitation by code
     */
    async deleteInvitationByCode(invitationCode: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // console.log('🗑️ [deleteInvitationByCode] Deleting invitation with code:', invitationCode)

        const { error } = await supabase
            .from('household_invitations')
            .delete()
            .eq('invitation_code', invitationCode)

        if (error) {
            console.error('❌ [deleteInvitationByCode] Error:', error)
            throw error
        }

        // console.log('✅ [deleteInvitationByCode] Invitation deleted successfully')
    },

    // Legacy API compatibility methods
    /**
     * @deprecated Use removeMember instead
     */
    async removeHouseholdMember(householdId: string, userId: string): Promise<void> {
        return this.removeMember(householdId, userId)
    },

    /**
     * @deprecated Use createInvitation instead - simplified version
     */
    async inviteParent(householdId: string, email: string): Promise<string> {
        return this.createInvitation(householdId, email)
    },

    /**
     * Accept invitation and create user account automatically (for new users)
     */
    async acceptInvitationAndCreateUser(invitationCode: string, userData: {
        name: string
        email?: string
        password?: string
    }): Promise<{ user: any; household: HouseholdRow }> {
        // console.log('🔍 [AcceptInvitationAndCreateUser] Looking for invitation code:', invitationCode.toUpperCase())

        // 1. Find the invitation
        const { data: invitation, error: invitationError } = await supabase
            .from('household_invitations')
            .select('*')
            .eq('invitation_code', invitationCode.toUpperCase())
            .eq('status', 'pending')
            .single()

        // console.log('🔍 [AcceptInvitationAndCreateUser] Invitation query result:', { invitation, invitationError })

        if (invitationError || !invitation) {
            console.error('❌ [AcceptInvitationAndCreateUser] Invitation not found:', invitationError)
            throw new Error('Code d\'invitation invalide ou expiré')
        }

        // Check if invitation is expired
        if (invitation.expires_at && new Date(invitation.expires_at) < new Date()) {
            throw new Error('Code d\'invitation expiré')
        }

        // console.log('✅ [AcceptInvitationAndCreateUser] Found invitation:', invitation.household_name)

        // 2. Create user account
        let authUser
        if (userData.email && userData.password) {
            // Create user with email/password
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: userData.email,
                password: userData.password,
                options: {
                    data: {
                        name: userData.name
                    }
                }
            })

            if (authError) throw authError
            authUser = authData.user
        } else {
            // Reuse existing anonymous session if available, otherwise create one
            const { data: { user: currentUser } } = await supabase.auth.getUser()
            if (currentUser && (currentUser as any).is_anonymous) {
                authUser = currentUser
            } else {
                const { data: authData, error: authError } = await supabase.auth.signInAnonymously()
                if (authError) throw authError
                authUser = authData.user
            }
        }

        if (!authUser) throw new Error('Failed to create user account')

        // 3. Create or update user profile
        const { error: profileError } = await supabase
            .from('user_profiles')
            .upsert({
                id: authUser.id,
                name: userData.name,
                email: userData.email || null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                language: detectLocale(),
                daily_reminder: false,
                point_assignments: false,
                reward_unlocked: false
            }, {
                onConflict: 'id'
            })

        if (profileError) {
            console.error('❌ [AcceptInvitationAndCreateUser] Error creating/updating profile:', profileError)
            throw profileError
        }

        // 4. Add user to household
        const { error: memberError } = await supabase
            .from('household_members')
            .insert({
                household_id: invitation.household_id,
                user_id: authUser.id,
                role: 'member',
                joined_at: new Date().toISOString(),
                created_at: new Date().toISOString(),
                created_by: invitation.inviter_id
            })

        if (memberError) {
            console.error('❌ [AcceptInvitationAndCreateUser] Error adding to household:', memberError)
            throw memberError
        }

        // 5. Mark invitation as accepted
        const { error: updateError } = await supabase
            .from('household_invitations')
            .update({
                status: 'accepted',
                accepted_at: new Date().toISOString(),
                invitee_id: authUser.id
            })
            .eq('id', invitation.id)

        if (updateError) {
            console.error('❌ [AcceptInvitationAndCreateUser] Error updating invitation:', updateError)
            // Don't throw - the main operation succeeded
        }

        // 6. Get household data
        const household = await this.getHousehold(invitation.household_id)
        if (!household) throw new Error('Household not found')

        // console.log('✅ [AcceptInvitationAndCreateUser] User created and added to household:', invitation.household_name)

        return {
            user: authUser,
            household
        }
    }
} 
